import {
  Box,
  Card,
  Checkbox,
  DialogContentText,
  FormControlLabel,
  Grid,
  Typography,
  useTheme
} from "@mui/material";
import * as React from "react";
import {useEffect, useState} from "react";
import {
  getISODateFromDate, getISODateTime, isoToLocalDate,
  isoToLocalDateString, numberToMoneyFormat, numberToPercentFormat
} from "../../../stores/StoreUtilities";
import {CreatePlanChangeInput, SnapshotType, UpdatePlanChangeInput} from "../../../API";
import {useResources} from "../../../stores/ResourceProvider";
import Plan from "../../../model/Plan";
import Model from "../../../model/Model";
import TimelineStrategyChange, {MilestoneChange} from "../../../model/changes/TimelineStrategyChange";
import PlanChange, {PlanChangeType} from "../../../model/PlanChange";
import ModelEditDialog from "../../../components/model/ModelEditDialog";
import Milestone, {MilestoneId} from "../../../model/Milestone";
import Person from "../../../model/Person";
import {addYears, isDate} from "date-fns";
import TextFieldValidator from "../../../components/form/TextFieldValidator";
import Tracking from "../../../components/Tracking";
import MilestoneDateFieldValidator from "../../../components/form/MilestoneDateFieldValidator";
import {FutureCalc, IPersonTimeline} from "../../../components/calculator/Calculator";
import DatePickerValidator from "../../../components/form/DatePickerValidator";
import FutureViewChart from "./charts/FutureViewChart";

const TimelineStrategyDialog = ({
  open,
  plan,
  model,
  onClose,
  onSave
}: {
  open? : boolean
  plan: Plan
  model: Model
  onClose?(): void
  onSave?(plan: Plan, update: PlanChange): void
}) => {
  const [planModel, setPlanModel] = useState<Plan>(plan)
  const [isOpen, setIsOpen] = useState<boolean>(open === true)
  const [timelineStrategyChange, setTimelineStrategyChange] = useState<TimelineStrategyChange | undefined>()
  const [name, setName] = useState<string>()
  const [description, setDescription] = useState<string>()
  const [person1, setPerson1] = useState<Person | undefined>()
  const [person2, setPerson2] = useState<Person | undefined>()
  const [defaults, setDefaults] = useState<Map<string, Milestone>>(new Map<string, Milestone>([]))
  const [retirement1, setRetirement1] = useState<string | undefined>(undefined)
  const [options1, setOptions1] = useState<Milestone[]>([])
  const [retirement2, setRetirement2] = useState<string | undefined>(undefined)
  const [options2, setOptions2] = useState<Milestone[]>([])
  const [lifeExpectancy1, setLifeExpectancy1] = useState<string | undefined>()
  const [lifeExpectancy2, setLifeExpectancy2] = useState<string | undefined>()
  const [commitChanges, setCommitChanges] = useState<boolean>(false)
  const [customMilestones, setCustomMilestones] = useState<MilestoneChange[]>([])
  const [defaultCalc, setDefaultCalc] = useState<FutureCalc | undefined>()
  const [futureCalc, setFutureCalc] = useState<FutureCalc | undefined>()

  const MaxAge = 120
  const { modelStore, calculator, notify } = useResources()

  const theme = useTheme()
  const styles = {
    card: {
      backgroundColor: theme.palette.background.paper,
      borderWidth: 1,
      borderColor: theme.palette.grey[500],
      borderStyle: "solid",
      borderRadius: "5px",
      padding: "0px 8px",
      marginTop: 1,
      marginBottom: 1,
      boxShadow: "rgba(0, 0, 0, 0.15) 0px 3px 3px 0px",
      touchAction: "manipulation"
    },
    tab: {
      marginRight: 2,
      paddingTop:0,
      paddingBottom: 0,
      color: theme.palette.text.secondary,
      fontSize: 14,
      lineHeight: 1.0,
      // maxHeight: 24
    },
    selectedTab: {
      marginRight: 2,
      paddingTop:0,
      paddingBottom: 0,
      color: theme.palette.primary.main,
      fontSize: 14,
      fontWeight: 700,
      lineHeight: 1.0,
      // maxHeight: 24
    },
    summaryLabel: {
      color: theme.palette.text.secondary,
      fontSize: 14,
      fontWeight: 700,
      lineHeight: 1.0,
    },
    summaryAmount: {
      color: theme.palette.text.primary,
      fontSize: 14,
      fontWeight: 700,
      lineHeight: 1.0,
      paddingLeft: 1
    },
    summaryAmountInc: {
      color: theme.palette.secondary.main,
      fontSize: 14,
      fontWeight: 700,
      lineHeight: 1.0,
      paddingLeft: 1
    },
    summaryAmountDec: {
      color: theme.palette.error.main,
      fontSize: 14,
      fontWeight: 700,
      lineHeight: 1.0,
      paddingLeft: 1
    }
  }

  useEffect(() => {
    setPlanModel(plan)
    setIsOpen(open === true)
    let change: TimelineStrategyChange = plan.getChange(PlanChangeType.TimelineStrategy) as TimelineStrategyChange
    if (!change) {
      change = new TimelineStrategyChange({
        accountId: plan.accountId,
        userId: model.userId,
        modelId: model.id,
        planId: plan.id,
        changeType: PlanChangeType.TimelineStrategy,
        name: "Timeline Strategy",
        description: "",
        enabled: true,
      })
    }
    setTimelineStrategyChange(change)
    setName("Timeline Strategy")
    setDescription(change.description)
    setDefaults(model.milestones)
    const custom = Array.from(model.milestones.values()).filter((m: Milestone) => m.isCustom)
      .map((m: Milestone) => new MilestoneChange({
        id: m.id,
        label: m.label,
      }))
    custom.sort((a: MilestoneChange, b: MilestoneChange) => a.label.localeCompare(b.label))

    if (model.persons.length > 0) {
      setPerson1(model.persons[0])
      const timeline = calculator.personTimeline(model.persons[0])
      if (timeline) {
        setOptions1(getOptions(timeline))
      }
    }
    if (model.persons.length > 1) {
      setPerson2(model.persons[1])
      const timeline = calculator.personTimeline(model.persons[1])
      if (timeline) {
        setOptions2(getOptions(timeline))
      }
    }

    let milestones = change.milestones
    if (milestones) {
      const r1 = milestones.get(MilestoneId.retirement1)
      if (r1) {
        setRetirement1(r1.date)
      }

      const r2 = milestones.get(MilestoneId.retirement2)
      if (r2) {
        setRetirement2(r2.date)
      }

      const l1 = milestones.get(MilestoneId.lifeExpectancy1)
      if (l1) {
        setLifeExpectancy1(String(l1.age))
      }

      const l2 = milestones.get(MilestoneId.lifeExpectancy2)
      if (l2) {
        setLifeExpectancy2(String(l2.age))
      }

      milestones.forEach((m: MilestoneChange) => {
        if (m.isCustom) {
          const customMilestone = custom.find((cm: MilestoneChange) => cm.id === m.id)
          if (customMilestone && m.date) {
            customMilestone.date = m.date
          }
        }
      })
      setCustomMilestones(custom)
    } else {
      setCustomMilestones(custom)
    }
    calculateDefault(model)
  }, [plan, open])

  useEffect(() => {
    const milestones: MilestoneChange[] = []
    if (retirement1) {
      const def = defaults.get(MilestoneId.retirement1)
      milestones.push(new MilestoneChange({
        id: MilestoneId.retirement1,
        label: def?.label,
        date: retirement1
      }))
    }
    if (retirement2) {
      const def = defaults.get(MilestoneId.retirement2)
      milestones.push(new MilestoneChange({
        id: MilestoneId.retirement2,
        label: def?.label,
        date: retirement2
      }))
    }
    if (lifeExpectancy1 && person1) {
      const age = Math.min(parseInt(lifeExpectancy1), MaxAge)
      const def = defaults.get(MilestoneId.lifeExpectancy1)
      milestones.push(new MilestoneChange({
        id: MilestoneId.lifeExpectancy1,
        label: def?.label,
        age: age,
        date: getISODateFromDate(addYears(new Date(person1!.birthDate), age))
      }))
    }
    if (lifeExpectancy2 && person2) {
      const age = Math.min(parseInt(lifeExpectancy2), MaxAge)
      const def = defaults.get(MilestoneId.lifeExpectancy2)
      milestones.push(new MilestoneChange({
        id: MilestoneId.lifeExpectancy2,
        label: def?.label,
        age: age,
        date: getISODateFromDate(addYears(new Date(person2!.birthDate), age))
      }))
    }

    calculateFuture([...milestones, ...customMilestones])
  }, [retirement1, retirement2, lifeExpectancy1, lifeExpectancy2, customMilestones])

  const getOptions = (timeline: IPersonTimeline): Milestone[] => {
    const ages = [62, 63, 64, 65, 66, 67, 68, 69, 70]
    const options: Milestone[] = ages.map((age: number) => {
      let label
      if (age === timeline.earlySocialSecurityAge) {
        label = `${age} (Early Social Security)`
      } else if (age === timeline.fullSocialSecurityAge) {
        label = `${age} (Full Social Security)`
      } else if (age === timeline.delayedSocialSecurityAge) {
        label = `${age} (Delayed Social Security)`
      } else {
        label = String(age)
      }
      const date = addYears(timeline.birthDate, age)
      return new Milestone({
        id: getISODateFromDate(date),
        label: label,
        date: date
      })
    })
    return options
  }

  const handleClose = async (event: any) => {
    if (onClose) {
      onClose()
    }
  }

  const handleSave = async (event: any) => {
    try {
      let change: PlanChange | undefined
      const milestones = []
      if (retirement1) {
        const def = defaults.get(MilestoneId.retirement1)
        milestones.push({
          id: MilestoneId.retirement1,
          label: def?.label,
          date: retirement1
        })
      }
      if (retirement2) {
        const def = defaults.get(MilestoneId.retirement2)
        milestones.push({
          id: MilestoneId.retirement2,
          label: def?.label,
          date: retirement2
        })
      }
      if (lifeExpectancy1 && person1) {
        const age = parseInt(lifeExpectancy1)
        const def = defaults.get(MilestoneId.lifeExpectancy1)
        milestones.push({
          id: MilestoneId.lifeExpectancy1,
          label: def?.label,
          age: age,
          date: getISODateFromDate(addYears(new Date(person1!.birthDate), age))
        })
      }
      if (lifeExpectancy2 && person2) {
        const age = parseInt(lifeExpectancy2)
        const def = defaults.get(MilestoneId.lifeExpectancy2)
        milestones.push({
          id: MilestoneId.lifeExpectancy2,
          label: def?.label,
          age: age,
          date: getISODateFromDate(addYears(new Date(person2!.birthDate), age))
        })
      }

      customMilestones.forEach((m: MilestoneChange) => {
        if (m.date) {
          milestones.push({
            id: m.id,
            label: m.label,
            date: m.date
          })
        }
      })

      if (timelineStrategyChange && !timelineStrategyChange.id) {
        const input: CreatePlanChangeInput = {
          accountId: timelineStrategyChange?.accountId,
          userId: timelineStrategyChange.userId,
          modelId: timelineStrategyChange.modelId,
          planId: timelineStrategyChange.planId,
          changeType: timelineStrategyChange.changeType,
          name: name ?? timelineStrategyChange.name,
          description: generateDescription(),
          enabled: !commitChanges,
          details: JSON.stringify(milestones)
        }
        change = await modelStore.createPlanChange(input, true)
        Tracking.event({action: "Timeline Strategy Created"})
      } else if (timelineStrategyChange) {
        const input: UpdatePlanChangeInput = {
          id: timelineStrategyChange.id,
          name: name,
          description: generateDescription(),
          enabled: !commitChanges,
          details: JSON.stringify(milestones)
        }
        change = await modelStore.updatePlanChange(input)
        Tracking.event({action: "Timeline Strategy Updated"})
      }

      if (change && commitChanges) {
        await change.commit(model, modelStore)
      }

      if (onSave && change) {
        onSave(plan, change)
      } else if (onClose) {
        onClose()
      }
    } catch (err: any) {
      notify.show('error', err.message)
    }
  }

  const generateDescription = () => {
    let array: string[] = []
    if (retirement1) {
      array.push(`${getMilestoneLabel(MilestoneId.retirement1)}: ${isoToLocalDateString(retirement1)}`)
    }
    if (retirement2) {
      array.push(`${getMilestoneLabel(MilestoneId.retirement2)}: ${isoToLocalDateString(retirement2)}`)
    }
    if (lifeExpectancy1) {
      array.push(`${getMilestoneLabel(MilestoneId.lifeExpectancy1)}: ${lifeExpectancy1}`)
    }
    if (lifeExpectancy2) {
      array.push(`${getMilestoneLabel(MilestoneId.lifeExpectancy2)}: ${lifeExpectancy2}`)
    }
    return array.join(", ")
  }

  const getMilestoneLabel = (id: MilestoneId) => {
    const milestone = defaults.get(id)
    if (milestone) {
      return milestone.label
    } else {
      return id
    }
  }

  const getMilestoneHelperText = (id: string, prop: 'age' | 'date') => {
    const milestone = defaults.get(id)
    if (milestone) {
      if (prop === 'age') {
        return `Default: ${String(milestone.age)}`
      } else {
        return `Default: ${isoToLocalDateString(milestone.date, "MM/dd/yyyy")}`
      }
    } else {
      return ""
    }
  }

  const setCustomMilestoneValue = (milestone: MilestoneChange, value: Date) => {
    const custom = customMilestones.find((m: MilestoneChange) => m.id === milestone.id)
    if (custom) {
      custom.date = isDate(value) ? getISODateFromDate(value) : ""
    }
    setCustomMilestones([...customMilestones])
  }

  const calculateDefault  = (model: Model) => {
    if (model && plan) {
      const endYear = model.person1?.latestLifeExpectancyYear
      let enabled = false
      const planChange = plan.getChange(PlanChangeType.TimelineStrategy)
      if (planChange) {
        // Temporarily disable the change
        enabled = planChange.enabled
        if (enabled) {
          planChange.enabled = false
        }
      }
      const calc = calculator.calculateFuture(model, "Default Future", endYear)
      setDefaultCalc(calc)
      if (planChange && enabled) {
        planChange.enabled = enabled
      }
    }
  }

  const calculateFuture = (milestones : MilestoneChange[]) => {
    if (timelineStrategyChange) {
      const change = new TimelineStrategyChange({
        id: timelineStrategyChange.id,
        accountId: timelineStrategyChange.accountId,
        userId: timelineStrategyChange.userId,
        modelId: timelineStrategyChange.modelId,
        planId: timelineStrategyChange.planId,
        changeType: timelineStrategyChange.changeType,
        enabled: true,
        details: JSON.stringify(milestones)
      })
      const modelCopy = modelStore.cloneModel(model.id)
      if (modelCopy) {
        // Grab default milestones from modelCopy
        setDefaults(structuredClone(modelCopy.milestones))

        const planCopy = modelCopy.findPlan(plan.id)
        if (planCopy) {
          planCopy.setChange(change)
          planCopy.applyChanges(modelCopy)
          const endYear = modelCopy.persons[0].latestLifeExpectancyYear
          console.log(`strategy endYear = ${endYear}`)
          modelStore.addCurrentSnapshot(modelCopy, SnapshotType.Combined)
          modelCopy.updatedAt = getISODateTime()
          const futureCalc = calculator.calculateFuture(modelCopy, "Optimized Future", endYear)
          setFutureCalc(futureCalc)
        }
      }
    }
  }

  const renderOptimizedSummary = () => {

    if (futureCalc && defaultCalc) {
      const net = Math.round(futureCalc.netLiquidAssets)
      const change = Math.round(futureCalc.netLiquidAssets - defaultCalc.netLiquidAssets)
      return (
        <Box sx={{display:"flex", justifyContent:"flex-end", pr:2}}>
          <Typography variant="body2" sx={styles.summaryLabel}>Net Liquid Assets:</Typography>
          <Typography variant="body2" sx={styles.summaryAmount}>{numberToMoneyFormat(net, 0)}</Typography>
          {change > 0 &&
            <Typography variant="body2" sx={styles.summaryAmountInc}> (+{numberToMoneyFormat(change, 0)})</Typography>
          }
          {change < 0 &&
            <Typography variant="body2" sx={styles.summaryAmountDec}> ({numberToMoneyFormat(change, 0)})</Typography>
          }
        </Box>
      )
    } else {
      return null
    }

  }

  const renderDefaultSummary = () => {

    if (defaultCalc) {
      const net = Math.round(defaultCalc.netLiquidAssets)
      return (
        <Box sx={{display:"flex", justifyContent:"flex-end", pr:2}}>
          <Typography variant="body2" sx={styles.summaryLabel}>Net Liquid Assets:</Typography>
          <Typography variant="body2" sx={styles.summaryAmount}>{numberToMoneyFormat(net, 0)}</Typography>
        </Box>
      )
    } else {
      return null
    }

  }

  if (!timelineStrategyChange) {
    return null
  }

  const commitDisabled = !(retirement1 || retirement2 || lifeExpectancy1 || lifeExpectancy2)

  return (
    <ModelEditDialog title="Timeline Strategy" open={isOpen} size="md"
                     onCancel={handleClose}
                     onSave={handleSave}
    >
      <DialogContentText sx={{mt:1}}>
        Override milestone dates and life expectancies.
      </DialogContentText>
      <Grid container spacing={2} sx={{mt: 0}}>
        <Grid item xs={4} sx={{maxHeight: 500, overflowY: "auto", pr: 1, pt: "0 !important"}}>
        {person1 &&
          <Card elevation={1} sx={styles.card}>
            <MilestoneDateFieldValidator
              name="retirement1"
              label={getMilestoneLabel(MilestoneId.retirement1)}
              milestones={options1}
              value={retirement1}
              helperText={getMilestoneHelperText(MilestoneId.retirement1, 'date')}
              sx={{paddingTop:"5px"}}
              onChange={(value: string) => {
                setRetirement1(value)
              }}
            />
          </Card>
        }
        {person1 &&
          <Card elevation={1} sx={styles.card}>
            <TextFieldValidator
              type="number"
              validators={{ required: false, minValue: 0, maxValue: MaxAge }}
              name="lifeExpectancy1"
              variant="standard"
              margin="dense"
              fullWidth
              label={getMilestoneLabel(MilestoneId.lifeExpectancy1)}
              value={lifeExpectancy1}
              helperText={getMilestoneHelperText(MilestoneId.lifeExpectancy1, 'age')}
              onChange={(event: any) => setLifeExpectancy1(event.target.value)}
            />
          </Card>
        }
        {person2 &&
          <Card elevation={1} sx={styles.card}>
            <MilestoneDateFieldValidator
              name="retirement2"
              label={getMilestoneLabel(MilestoneId.retirement2)}
              milestones={options2}
              value={retirement2}
              helperText={getMilestoneHelperText(MilestoneId.retirement2, 'date')}
              sx={{paddingTop:"5px"}}
              onChange={(value: string) => {
                setRetirement2(value)
              }}
            />
          </Card>
        }
        {person2 &&
          <Card elevation={1} sx={styles.card}>
            <TextFieldValidator
              type="number"
              validators={{ required: false, minValue: 0, maxValue: MaxAge }}
              name="lifeExpectancy2"
              variant="standard"
              margin="dense"
              fullWidth
              label={getMilestoneLabel(MilestoneId.lifeExpectancy2)}
              value={lifeExpectancy2}
              helperText={getMilestoneHelperText(MilestoneId.lifeExpectancy2, 'age')}
              onChange={(event: any) => setLifeExpectancy2(event.target.value)}
            />
          </Card>
        }
          {customMilestones.map((m: MilestoneChange) =>
            <Card elevation={1} sx={styles.card} key={m.id}>
              <DatePickerValidator
                name={m.id}
                label={m.label}
                value={m.date ? isoToLocalDate(m.date) : undefined }
                format="MM-dd-yyyy"
                variant="standard"
                margin="dense"
                sx={{paddingTop:"6px"}}
                fullWidth
                helperText={getMilestoneHelperText(m.id, 'date')}
                onChange={(newValue: any) => {
                  setCustomMilestoneValue(m, newValue)
                }}
              />
            </Card>

          )}
      </Grid>
      <Grid item xs={8} sx={{pt: "0 !important"}}>
        <Grid container spacing={2} direction="column">
          <Grid item height={450}>
            {futureCalc &&
              <Box mt={1}>
                <Box sx={{display: "flex",  flexDirection: "row", justifyContent: "space-between"}}>
                  <Typography variant="h6" sx={styles.selectedTab}>Optimized Future View</Typography>
                  {renderOptimizedSummary()}
                </Box>
                <FutureViewChart futureCalcs={[futureCalc]} year={0}
                                 height={"200"} color={"rgb(51, 122, 183)"}/>
              </Box>
            }
            {defaultCalc &&
              <Box mt={1}>
                <Box sx={{display: "flex",  flexDirection: "row", justifyContent: "space-between"}}>
                  <Typography variant="h6" sx={styles.selectedTab}>Default Future View</Typography>
                  {renderDefaultSummary()}
                </Box>
                <FutureViewChart futureCalcs={[defaultCalc]} year={0}
                                 height={"200"}/>
              </Box>
            }
          </Grid>
          <Grid item>
            <FormControlLabel control={
              <Checkbox checked={commitChanges}
                        disabled={commitDisabled}
                        onChange={(event: any) => {
                          setCommitChanges(event.target.checked)
                        }}
              />}
              label={`Permanently apply these changes to the Timeline on Save.`}
              sx={{marginTop:1, marginLeft:1}}
              componentsProps={{
                typography: {
                  variant:"body2"
                }}
              }
            />
          </Grid>
        </Grid>
      </Grid>
      </Grid>
    </ModelEditDialog>
  )
}

export default TimelineStrategyDialog